/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * @copyright Copyright 2021-2024 Epic Systems Corporation
 * @file Homegrown (customer) apps dashboard widget
 * @module Epic.AppOrchard.Dashboard.Widgets.HomegrownAppsWidget
 */

import { Checkbox } from "@chakra-ui/checkbox";
import { Box } from "@chakra-ui/layout";
import { Text } from "@chakra-ui/react";
import { ISuccessHandler, useAsync } from "@epic/react-async-hook";
import { useDispatch } from "@epic/react-redux-booster";
import { config } from "ao/appConfig";
import { AsyncPlaceholder, ITab, SearchInput, TabbedSection } from "ao/components/Core";
import { getHomegrownApps } from "ao/components/Dashboard/data";
import { useHomegrownAppsState } from "ao/state/widgets/homegrownApps";
import { updateAppsAfterLoad } from "ao/store-methods/homegrownApps";
import { IWidget, IWidgetProps } from "ao/types/dashboard";
import { getBaseUrl } from "ao/utils/helpers";
import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { WidgetSpinner, WidgetTable } from "../../widgetHelpers";
import { HomegrownAppRow } from "./helperComponents/HomegrownAppRow";

function getNumFiltersApplied(...args: boolean[]): number {
	return args.filter((a) => a === true).length;
}

/** Homegrown (customer) apps dashboard widget */
export const HomegrownAppsWidget: IWidget = (props: IWidgetProps) => {
	const {
		isExpanded,
		orgId,
		userSecurity,
		onFiltersUpdate,
		onNumFiltersAppliedUpdate,
		numFiltersApplied,
	} = props;

	//#region state/handlers
	const [searchTerm, setSearchTerm] = useState<string>("");
	const handleChangeSearchTerm = useCallback((newSearchTerm: string) => setSearchTerm(newSearchTerm), []);
	const [createdByCurrentUserOnly, setCreatedByCurrentUserOnly] = useState<boolean>(false);
	const [changeRequestsOnly, setChangeRequestsOnly] = useState<boolean>(false);
	const [includeAllRelatedOrgApps, setIncludeAllRelatedOrgApps] = useState<boolean>(false);

	const handleCreatedByCurrentUserChecked = useCallback(
		(ev: ChangeEvent<HTMLInputElement>) => {
			const createdByCurrentUserChecked = ev.target.checked;
			onNumFiltersAppliedUpdate &&
				onNumFiltersAppliedUpdate(
					getNumFiltersApplied(
						createdByCurrentUserChecked,
						changeRequestsOnly,
						includeAllRelatedOrgApps,
					),
				);
			setCreatedByCurrentUserOnly(createdByCurrentUserChecked);
		},
		[changeRequestsOnly, includeAllRelatedOrgApps, onNumFiltersAppliedUpdate],
	);

	const handleChangeRequestsOnlyChecked = useCallback(
		(ev: ChangeEvent<HTMLInputElement>) => {
			const changeRequestsOnlyChecked = ev.target.checked;
			onNumFiltersAppliedUpdate &&
				onNumFiltersAppliedUpdate(
					getNumFiltersApplied(
						createdByCurrentUserOnly,
						changeRequestsOnlyChecked,
						includeAllRelatedOrgApps,
					),
				);
			setChangeRequestsOnly(changeRequestsOnlyChecked);
		},
		[createdByCurrentUserOnly, includeAllRelatedOrgApps, onNumFiltersAppliedUpdate],
	);

	const handleIncludeAllRelatedOrgAppsChecked = useCallback(
		(ev: ChangeEvent<HTMLInputElement>) => {
			const includeAllRelatedOrgApps = ev.target.checked;
			onNumFiltersAppliedUpdate &&
				onNumFiltersAppliedUpdate(
					getNumFiltersApplied(
						createdByCurrentUserOnly,
						changeRequestsOnly,
						includeAllRelatedOrgApps,
					),
				);
			setIncludeAllRelatedOrgApps(includeAllRelatedOrgApps);
		},
		[createdByCurrentUserOnly, changeRequestsOnly, onNumFiltersAppliedUpdate],
	);

	const baseUrl = useMemo(() => getBaseUrl(), []);
	const entriesPerPage = config.Dashboard.WidgetAppRowsPerPage;
	const dispatch = useDispatch();

	const hasLoaded = useHomegrownAppsState((selectors) => selectors.getHasLoaded(orgId), [orgId]);

	const searchParams = {
		orgId,
		searchTerm,
		currentUserId: userSecurity.userId!,
		createdByCurrentUserOnly,
		changeRequestsOnly,
		includeAllRelatedOrgApps,
	};

	const draftApps = useHomegrownAppsState((selectors) => selectors.getDraftApps(searchParams), [
		searchParams,
	]);

	const activatedForTesting = useHomegrownAppsState(
		(selectors) => selectors.getAppsActivatedForTesting(searchParams),
		[searchParams],
	);

	const activatedForProd = useHomegrownAppsState(
		(selectors) => selectors.getAppsActivatedForProd(searchParams),
		[searchParams],
	);

	const currentEpicVersion = useHomegrownAppsState(
		(selectors) => selectors.getOrgCurrentEpicVersion(orgId),
		[orgId],
	);

	const isLocalJKUWarningEnabledForFeb24 = useHomegrownAppsState(
		(selectors) => selectors.getIsLocalJKUWarningEnabledForFeb24(),
		[],
	);

	const handleAppsLoadedSuccess = useCallback<ISuccessHandler<typeof getHomegrownApps>>(
		(response, extras) => {
			const [orgId] = extras.params;
			updateAppsAfterLoad(
				dispatch,
				orgId,
				response.homegrownApps,
				response.orgCurrentEpicVersion,
				response.isLocalJKUWarningEnabledForFeb24,
			);
		},
		[dispatch],
	);

	const [LoadingPlaceholder, loadApps] = useAsync(getHomegrownApps, AsyncPlaceholder, {
		executeImmediately: false,
		displayName: "getHomegrownApps",
		// `extras` will contain `params` field which has the params passed to getInProgressApps (namely the org ID)
		// this is needed rather than using the orgId from props directly in handleAppsLoaded because
		// that will result in a stale closure (https://gitlab.epic.com/foundations/react-async-hook/-/issues/2)
		onSuccess: (response, extras) => handleAppsLoadedSuccess(response, extras),
	});

	const filters = useMemo(
		() => (
			<>
				{userSecurity.isEpicCustomer && (
					<Checkbox
						isChecked={createdByCurrentUserOnly}
						onChange={handleCreatedByCurrentUserChecked}
						display={isExpanded ? "inline-flex" : "flex"}
					>
						Apps I created
					</Checkbox>
				)}
				<Checkbox
					isChecked={changeRequestsOnly}
					onChange={handleChangeRequestsOnlyChecked}
					ml={isExpanded && userSecurity.isEpicCustomer ? "1em" : undefined}
					display={isExpanded ? "inline-flex" : "flex"}
				>
					Apps with changes requested
				</Checkbox>
				{(userSecurity.isEpicUser || userSecurity.canViewAllRelatedOrgApps) && (
					<Checkbox
						isChecked={includeAllRelatedOrgApps}
						onChange={handleIncludeAllRelatedOrgAppsChecked}
						ml={isExpanded ? "1em" : undefined}
						display={isExpanded ? "inline-flex" : "flex"}
						title="Include all apps created by related customer orgs, even if they haven't been activated. Apps from related customer orgs that have been activated for your system will always show."
					>
						Include all related org apps
					</Checkbox>
				)}
			</>
		),
		[
			changeRequestsOnly,
			createdByCurrentUserOnly,
			handleChangeRequestsOnlyChecked,
			handleCreatedByCurrentUserChecked,
			handleIncludeAllRelatedOrgAppsChecked,
			includeAllRelatedOrgApps,
			isExpanded,
			userSecurity.canViewAllRelatedOrgApps,
			userSecurity.isEpicCustomer,
			userSecurity.isEpicUser,
		],
	);

	useEffect(() => {
		// if/when this issue (https://gitlab.epic.com/foundations/react-async-hook/-/issues/3) is fixed
		// we can use !hasLoaded as a executeImmediately param for useAsync instead to be a bit cleaner
		if (!hasLoaded) {
			loadApps(orgId);
		}

		// clear filters if user clicked clear filters button in parent
		if (numFiltersApplied === 0) {
			if (changeRequestsOnly) {
				setChangeRequestsOnly(false);
			}
			if (createdByCurrentUserOnly) {
				setCreatedByCurrentUserOnly(false);
			}

			if (includeAllRelatedOrgApps) {
				setIncludeAllRelatedOrgApps(false);
			}
		}

		// tell parent we have filters and how many are applied
		onFiltersUpdate && onFiltersUpdate(filters);
	}, [
		hasLoaded,
		loadApps,
		orgId,
		numFiltersApplied,
		changeRequestsOnly,
		createdByCurrentUserOnly,
		onFiltersUpdate,
		filters,
		includeAllRelatedOrgApps,
	]);

	//#endregion

	const tabs: ITab[] = [
		{
			tabName: "In Dev",
			countPrefix: draftApps.length,
			helptext:
				"Apps created by users from your organization that have not been activated for testing or production.",
			content: (
				<WidgetTable
					noRecordsMessage="No matching apps"
					entriesPerPage={!isExpanded ? entriesPerPage : undefined}
					isExpanded={isExpanded}
				>
					{draftApps.map((app) => (
						<HomegrownAppRow
							dispatch={dispatch}
							app={app}
							key={app.appId}
							orgId={orgId}
							baseUrl={baseUrl}
							userSecurity={userSecurity}
							orgCurrentEpicVersion={currentEpicVersion || -1}
							isLocalJKUWarningEnabled={isLocalJKUWarningEnabledForFeb24}
						></HomegrownAppRow>
					))}
				</WidgetTable>
			),
		},
		{
			tabName: "In Testing",
			countPrefix: activatedForTesting.length,
			helptext:
				"Apps created by users from your organization that are activated for testing in non-production",
			content: (
				<WidgetTable
					noRecordsMessage="No matching apps"
					entriesPerPage={!isExpanded ? entriesPerPage : undefined}
					isExpanded={isExpanded}
				>
					{activatedForTesting.map((app) => (
						<HomegrownAppRow
							dispatch={dispatch}
							app={app}
							orgId={orgId}
							userSecurity={userSecurity}
							key={app.appId}
							baseUrl={baseUrl}
							orgCurrentEpicVersion={currentEpicVersion || -1}
							isLocalJKUWarningEnabled={isLocalJKUWarningEnabledForFeb24}
						></HomegrownAppRow>
					))}
				</WidgetTable>
			),
		},
		{
			tabName: "In Production",
			countPrefix: activatedForProd.length,
			helptext: "Apps created by users from your organization that are activated for Production use",
			content: (
				<WidgetTable
					noRecordsMessage="No matching apps"
					entriesPerPage={!isExpanded ? entriesPerPage : undefined}
					isExpanded={isExpanded}
				>
					{activatedForProd.map((app) => (
						<HomegrownAppRow
							dispatch={dispatch}
							app={app}
							orgId={orgId}
							userSecurity={userSecurity}
							key={app.appId}
							baseUrl={baseUrl}
							orgCurrentEpicVersion={currentEpicVersion || -1}
							isLocalJKUWarningEnabled={isLocalJKUWarningEnabledForFeb24}
						></HomegrownAppRow>
					))}
				</WidgetTable>
			),
		},
	];

	return (
		<>
			{isExpanded && ( //show search bar if in expanded/popup mode
				<SearchInput
					placeholder="Search for apps by app name or client ID"
					onChangeCallback={handleChangeSearchTerm}
					inputGroupProps={{ mb: "1rem" }}
				></SearchInput>
			)}
			<LoadingPlaceholder
				inProgressIndicator={<WidgetSpinner />}
				errorMessage="Error loading apps from the server"
				useLastRequestForErrors
			>
				{isExpanded && (
					<>
						<Box mb="0.75em">
							<Text as="span" mr="0.5em">
								Filter on:{" "}
							</Text>
							{filters}
						</Box>
					</>
				)}
				<TabbedSection tabs={tabs} />
			</LoadingPlaceholder>
		</>
	);
};

HomegrownAppsWidget.descriptor = "homegrownApps";
